import React, { useRef, useState, useCallback } from 'react'
import clsx from 'clsx'

import toArray from 'utils/toolset/toArray'
import KeyboardKey from 'utils/toolset/keyboard'
import pluralize from 'utils/toolset/pluralize'

import { Text } from 'components/Text'

import { useDragDropFileContext } from '../DragDropFile.context'

import { DropZoneWrapper, HiddenInput, UploadIcon } from '../styles'
import type { DropZoneProps } from '../types'

const DropZone = ({
  className,
  hint,
  multiple = false,
  accept,
  disabled = false,
  error = false,
  renderCustomContent,
  ...props
}: DropZoneProps): JSX.Element => {
  const { onFilesAdded } = useDragDropFileContext()

  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const onChangeInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault()
      event.stopPropagation()

      onFilesAdded(Array.from(event.currentTarget.files || []))
    },
    [onFilesAdded]
  )

  const onKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (!disabled && inputRef.current && KeyboardKey(event).is('ENTER')) {
        inputRef.current.click()
      }
    },
    [disabled, inputRef]
  )

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()

      if (!disabled) {
        if (isDragging) {
          setIsDragging(false)
        }

        onFilesAdded(Array.from(event.dataTransfer.files || []))
      }
    },
    [disabled, isDragging, onFilesAdded]
  )

  const onDragStart = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }, [])

  const onDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()

      if (!disabled && !isDragging) {
        setIsDragging(true)
      }
    },
    [disabled, isDragging]
  )

  const onDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()

      if (isDragging) {
        setIsDragging(false)
      }
    },
    [isDragging]
  )

  const renderDefaultContent = useCallback(
    () => (
      <>
        <UploadIcon name="upload" size={48} />
        <Text variant="body" color="color-neutral-darker">
          {`Drag and drop your ${pluralize(Number(!multiple), 'files', 'file')} here or `}
          <Text as="a" color={disabled ? 'color-neutral' : 'color-accent'} variant="body-bold">
            {`Browse ${pluralize(Number(!multiple), 'Files', 'File')}`}
          </Text>
        </Text>
        {hint && (
          <Text variant="caption" color="color-neutral-dark">
            {hint}
          </Text>
        )}
      </>
    ),
    [multiple, disabled, hint]
  )

  return (
    <DropZoneWrapper
      space="m"
      align="center"
      justify="center"
      className={clsx(className, {
        'is-disabled': disabled,
        'is-focused': isDragging,
      })}
      disabled={disabled}
      error={error}
      tabIndex={0}
      role="button"
      aria-pressed="false"
      onKeyPress={onKeyPress}
      onDrop={onDrop}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      {(renderCustomContent || renderDefaultContent)({ isDragging })}
      <HiddenInput
        onInput={onChangeInput}
        type="file"
        ref={inputRef}
        accept={accept ? toArray(accept).join(',') : undefined}
        disabled={disabled}
        multiple={multiple}
        {...props}
      />
    </DropZoneWrapper>
  )
}

export default DropZone
